import type { ChipOption } from '@/components/ChipSelect';
import { type TaskCard, type TaskKind, type TaskPrStatus, type TaskSize, type TaskType } from '@shared/types/tasks';
import type { StatusMeta } from '@/components/status-tone';

/** A task's type (T-331): its own, else the Board's default (a card made before the types loaded). */
export function taskTypeOf(types: TaskType[], task: Pick<TaskCard, 'type_id'>): TaskType | undefined {
  return types.find((t) => t.id === task.type_id) ?? types.find((t) => t.is_default);
}

export const TASK_SIZE_LABELS: Record<TaskSize, string> = { XS: 'XS', S: 'S', M: 'M', L: 'L', XL: 'XL' };

export const TASK_KIND_LABELS: Record<TaskKind, string> = {
  fix: 'Fix',
  performance: 'Performance',
  feature: 'Feature',
  chore: 'Chore',
  docs: 'Docs',
  refactor: 'Refactor',
  ci: 'CI',
};

export const TASK_FIELD_LABELS = { size: 'Size', kind: 'Kind', prs: 'Pull requests' } as const;

/** A pull request's status, coloured as everywhere else (status-tone). */
export const TASK_PR_STATUSES: Record<TaskPrStatus, StatusMeta> = {
  draft: { label: 'Draft', tone: 'neutral' },
  open: { label: 'Open', tone: 'info' },
  merged: { label: 'Merged', tone: 'success' },
  closed: { label: 'Closed', tone: 'danger' },
};

/** "#225" for a GitHub pull request address, else the address without its scheme. */
export function prLabel(url: string): string {
  const number = /\/pull\/(\d+)/.exec(url)?.[1];
  return number ? `#${number}` : url.replace(/^https?:\/\//, '');
}

/** A size as a chip, on the task and its card: cool to hot as the work grows. */
export const TASK_SIZE_CHIPS: Record<TaskSize, ChipOption> = {
  XS: { label: 'Size XS', colour: 'teal' },
  S: { label: 'Size S', colour: 'green' },
  M: { label: 'Size M', colour: 'amber' },
  L: { label: 'Size L', colour: 'orange' },
  XL: { label: 'Size XL', colour: 'red' },
};

/** A kind as a chip: a fix in red, a feature in blue, upkeep in quieter colours. */
export const TASK_KIND_CHIPS: Record<TaskKind, ChipOption> = {
  fix: { label: TASK_KIND_LABELS.fix, colour: 'red' },
  performance: { label: TASK_KIND_LABELS.performance, colour: 'amber' },
  feature: { label: TASK_KIND_LABELS.feature, colour: 'blue' },
  chore: { label: TASK_KIND_LABELS.chore, colour: 'gray' },
  docs: { label: TASK_KIND_LABELS.docs, colour: 'teal' },
  refactor: { label: TASK_KIND_LABELS.refactor, colour: 'violet' },
  ci: { label: TASK_KIND_LABELS.ci, colour: 'pink' },
};
